import { useState, ButtonHTMLAttributes } from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import { useKeenSlider } from 'keen-slider/react';
import gsap from 'gsap';
import s from './slider.module.css';
import 'keen-slider/keen-slider.min.css';
import { ImageProps } from '../utils';

type EventsSliderProps = {
  images: Array<ImageProps>;
};

export const EventsSlider = (props: EventsSliderProps) => {
  const [loaded, setLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [sliderRef, slider] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 1,
      spacing: 108
    },
    loop: true,
    created() {
      setLoaded(true);
    },
    slideChanged(_slider) {
      setCurrentSlide(_slider.track.details.rel);
    },
    dragStarted: () => {
      gsap.to(slider.current.container.querySelectorAll('.keen-slider__slide > img'), {
        scale: 0.95,
        stagger: {
          each: 0.02,
          from: 'start'
        }
      });
      slider.current.container.style.cursor = 'grabbing';
    },
    dragEnded: () => {
      gsap.to(slider.current.container.querySelectorAll('.keen-slider__slide > img'), {
        scale: 1,
        stagger: {
          each: 0.02,
          from: 'start'
        }
      });
      slider.current.container.style.cursor = 'grab';
    }
  });

  return (
    <div className="relative">
      <div
        ref={sliderRef}
        className={clsx(
          'keen-slider h-[85vw] max-h-[320px] w-auto md:h-[33vw] md:max-h-[500px] rounded-2xl lg:rounded-lg',
          s.slider
        )}
      >
        {props.images.map((attr, idx) => (
          <figure
            key={idx}
            className="keen-slider__slide relative rounded-lg overflow-hidden w-full"
          >
            <Image {...attr} fill className="object-cover" sizes="(min-width: 1920px) 60vw, 85vw" />
          </figure>
        ))}
      </div>
      {loaded && slider.current && (
        <>
          <Arrow
            className="absolute left-[18px] top-1/2 -translate-y-1/2"
            onClick={e => {
              e.stopPropagation();
              slider.current?.prev();
            }}
          />

          <Arrow
            className="absolute right-[18px] top-1/2 -translate-y-1/2"
            right
            onClick={e => {
              e.stopPropagation();
              slider.current?.next();
            }}
          />
          <div className="flex gap-2 mx-auto w-fit mt-7">
            {[...Array(slider.current.track.details.slides.length).keys()].map(idx => {
              return (
                <button
                  key={idx}
                  onClick={() => {
                    slider.current?.moveToIdx(idx);
                  }}
                  aria-label={`Move to slide number ${idx}`}
                  className={clsx(
                    idx === currentSlide && '!bg-white',
                    'transition-colors duration-300 h-[10px] w-[10px] rounded-full bg-[#37383B]'
                  )}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

type ArrowProps = ButtonHTMLAttributes<HTMLButtonElement> & { right?: boolean };

export const Arrow = ({ className, right, ...props }: ArrowProps) => {
  return (
    <button
      {...props}
      type="button"
      aria-label={right ? 'next' : 'previous'}
      className={clsx(
        'hidden lg:flex rounded-full z-10 items-center justify-center h-10 w-10 bg-[#101010]',
        className
      )}
    >
      <svg
        className={clsx(right && 'rotate-180')}
        width="24"
        height="25"
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M19.0002 12.5L5.0002 12.5"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.0002 5.49976L5.0002 12.4998L12.0002 19.4998"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
};
